import React, { useEffect, useState } from "react";
import classnames from "classnames";
import withReduxProvider from "web/react/redux-provider";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import Button from "web/react/components/button/button";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { consentToAllCookies } from "web/redux/ducks/cookie-consent";
import history from "web/script/utils/history";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import environment from "web/script/modules/environment";
import { ReduxStoreState } from "web/redux/store";
import styles from "./cookie-banner.css";

function extractConsentState(state: ReduxStoreState): boolean {
    return state.cookieConsentReducer.consentConfirmed;
}

interface CookieBannerProps {
    cookiePolicyUrl: string;
    debug?: boolean;
}

export function CookieBanner({ cookiePolicyUrl, debug }: CookieBannerProps): JSX.Element {
    const showCookieSettingsLink = environment.getFeature("mg_require_cookie_consent");
    const enhancedAcceptAll = environment.getFeature("mg_cookie_consent_larger_cta");
    const showConsentSettings = environment.getFeature("mg_cookie_consent_settings");
    let [showBanner, setShowBanner] = useState<boolean>(false);
    const consentConfirmed = useReduxSelector(extractConsentState);
    const dispatch = useDispatch();

    function hideNotification(): void {
        setShowBanner(false);
        dispatch(consentToAllCookies());
        analytics.event("Cookie consent", "Clicked", "Hide");
    }

    useEffect(() => {
        if (debug || !consentConfirmed) {
            analytics.event("Cookie consent", "Shown", undefined, true);
            setShowBanner(true);
        } else {
            setShowBanner(false);
        }
    }, [debug, consentConfirmed]);

    let cookieText = gettext("cookie_notification.description_v2");

    const cookieSettingsModal = "cookie-settings-banner-modal";
    const settingsModalIsOpen = !!useOverlayWatcher(cookieSettingsModal);

    function onSettingsModalClose(): void {
        history.popOverlay(cookieSettingsModal);
    }

    function openSettingsModal(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != cookieSettingsModal) {
            history.pushOverlay(cookieSettingsModal);
        }
    }

    function getAcceptButton(): JSX.Element {
        if (enhancedAcceptAll && showCookieSettingsLink) {
            return (
                <Button
                    styleType={"cta"}
                    onClick={hideNotification}
                    className={styles.acceptButton}
                    fullWidth
                    fullHeight={false}
                    openInNewTab={false}
                    disabled={false}
                >
                    {gettext("cookie_notification.cookie_settings.accept_all.cta")}
                </Button>
            );
        } else if (enhancedAcceptAll && !showCookieSettingsLink) {
            return (
                <Button
                    styleType={"cta"}
                    onClick={hideNotification}
                    className={styles.acceptButton}
                    fullWidth
                    fullHeight={false}
                    openInNewTab={false}
                    disabled={false}
                >
                    {gettext("general.accept")}
                </Button>
            );
        }

        return (
            <SecondaryButton onClick={hideNotification} className={styles.acceptButton} fullHeight>
                {gettext("general.accept")}
            </SecondaryButton>
        );
    }
    const CookieSettings = (
        <div
            className={classnames(styles.cookieSettingsWrapper, {
                [styles.inlineSettings]: showCookieSettingsLink && enhancedAcceptAll,
            })}
        >
            <CookieSettingsContainer
                isOpen={settingsModalIsOpen}
                onModalClose={onSettingsModalClose}
            />
            <a className={styles.link} onClick={openSettingsModal}>
                {" "}
                {// "Cookie Settings"
                gettext("cookie_notification.cookie_settings.title")}
            </a>
        </div>
    );

    const ReadMoreLink = (
        <a className={styles.readMore} href={cookiePolicyUrl}>
            {gettext("general.read_more")}
        </a>
    );
    return (
        <>
            {showBanner && (
                <div
                    className={classnames(styles.wrapper, {
                        [styles.wideWrapper]: enhancedAcceptAll,
                    })}
                >
                    <div
                        className={classnames(styles.container, {
                            [styles.wideContainer]: enhancedAcceptAll,
                        })}
                    >
                        <div
                            className={classnames(styles.content, {
                                [styles.wideContent]: enhancedAcceptAll,
                            })}
                        >
                            <div className={styles.text}>
                                {cookieText}{" "}
                                {(!showCookieSettingsLink || !enhancedAcceptAll) && ReadMoreLink}
                                {showConsentSettings &&
                                    showCookieSettingsLink &&
                                    enhancedAcceptAll &&
                                    CookieSettings}
                            </div>
                        </div>
                        <div
                            className={classnames(styles.options, {
                                [styles.wideAcceptButton]: enhancedAcceptAll,
                            })}
                        >
                            {showConsentSettings &&
                                showCookieSettingsLink &&
                                !enhancedAcceptAll &&
                                CookieSettings}
                            {getAcceptButton()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default withReduxProvider(CookieBanner);
